<template>
  <div class="order-detail-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="back-btn" @click="toBack">
          <i class="el-icon-arrow-left"></i>
          <span>返回</span>
        </div>
        <div class="page-title">{{ activeTitle }}</div>
      </div>
    </div>

    <!-- 主体内容 -->
    <div class="page-content" id="pageContentId">
      <!-- 步骤指示器 -->
      <div class="step-indicator" v-if="[1, 2, 3, 4, 9, 10, 11].includes(activeStep)">
        <div class="step-container">
          <div v-for="(item, index) in stepOptions" :key="item.id" class="step-item">
            <template v-if="index < 6">
              <div class="step-dot" :class="{ active: activeStep === item.id }"></div>
              <span class="step-text" :class="{ active: activeStep === item.id }">{{ item.name }}</span>
              <div class="step-connector" v-if="index < 5"></div>
            </template>
          </div>
        </div>
      </div>

      <!-- 待付款倒计时 -->
      <div class="countdown-section" v-if="[9].includes(activeStep) && orderInfo.toCancelSecond">
        <div class="countdown-card">
          <div class="countdown-content">
            <div class="countdown-icon">
              <i class="el-icon-alarm-clock"></i>
            </div>
            <div class="countdown-info">
              <div class="countdown-text">请尽快支付货款！</div>
              <div class="countdown-timer">
                <span>请您在&nbsp;</span>
                <Countdown color="#fff" :timeValue="orderInfo.toCancelSecond ? orderInfo.toCancelSecond * 1000 : 0" />
                <span>&nbsp;内完成支付，否则订单会被自动取消</span>
              </div>
            </div>
            <div class="countdown-action">
              <el-button type="warning" class="pay-btn" round @click="toPayFn">去支付(F9)</el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 订单基本信息 -->
      <div class="order-basic-info" v-if="[1, 9, 2, 3, 10, 4, 5, 6, 7].includes(activeStep)">
        <div class="info-card">
          <div class="info-header">
            <div class="order-code">订单号：{{ orderInfo.orderCode }}</div>
            <div class="submit-info">
              <span class="submit-by">提交人：{{ orderInfo.submitByName }}</span>
              <span class="submit-time">提交时间：{{ orderInfo.submitAt }}</span>
            </div>
          </div>
          <div class="info-footer">
            <span class="buyer-name" v-if="orderInfo.buyerName">买家名称：{{ orderInfo.buyerName }}</span>
          </div>
        </div>
      </div>

      <!-- 收货地址 -->
      <div class="address-section">
        <div class="section-card">
          <div class="card-header">
            <div class="card-title">
              <i class="el-icon-location-information"></i>
              <span>收货地址</span>
            </div>
          </div>
          <div class="card-content">
            <div v-if="[11].includes(activeStep)">
              <EditAddress @select="selectAddress" :addressInfo="addressInfo" />
            </div>
            <div v-else class="address-display">
              <div class="address-main">
                <span class="receiver">{{ orderInfo.receiver }}</span>
                <span class="receiver-phone">{{ orderInfo.receiverPhone }}</span>
              </div>
              <div class="address-detail">
                <span>{{ orderInfo.receiverAreaName }}</span>
                <span>{{ orderInfo.receiverAddress }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 商品明细 -->
      <div class="goods-section">
        <div class="section-card">
          <div class="card-header">
            <div class="card-title">
              <i class="el-icon-shopping-bag-1"></i>
              <span>商品明细</span>
            </div>
          </div>
          <div class="card-content">
            <OrderDetailItem
              :table="goodsDetailsTable"
              :isShow="false"
              :showOriginalPrice="[11].includes(activeStep) ? false : true"
              @change="tableDataChange"
            >
              <template #footer>
                <div class="goods-summary">
                  <div class="summary-row">
                    <span>共{{ totalCategories }}种，{{ totalQty }}件商品</span>
                    <span class="total-amount"
                      >合计：¥{{ [11].includes(activeStep) ? totalAmount : finalAmountFn() }}</span
                    >
                  </div>
                  <div class="summary-row discount-info" v-if="Number(orderInfo.concessionsAmount) > 0">
                    <span class="original-price">
                      原价：{{ Number(orderInfo.finalOriginalPrice) ? toFixedFn(orderInfo.finalOriginalPrice) : 0 }}
                    </span>
                    <span class="discount-amount">已优惠：{{ toFixedFn(orderInfo.concessionsAmount) }}</span>
                  </div>
                </div>
              </template>
            </OrderDetailItem>
          </div>
        </div>
      </div>

      <!-- 订单信息 -->
      <div class="order-info-section">
        <div class="section-card">
          <div class="card-header">
            <div class="card-title">
              <i class="el-icon-document"></i>
              <span>订单信息</span>
            </div>
          </div>
          <div class="card-content">
            <div class="order-info-content">
              <div class="info-column">
                <div v-if="[11].includes(activeStep)">
                  <div class="info-item">
                    <span class="info-label">结算方式：</span>
                    <span class="info-value">{{ paymentType }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">发票类型：</span>
                    <span class="info-value">{{ invoiceType }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">提货方式：</span>
                    <el-select v-model="orderInfo.deliveryType" placeholder="请选择提货方式" size="small">
                      <el-option v-for="(item, index) in deliveryType" :key="index" :label="item" :value="item">
                      </el-option>
                    </el-select>
                  </div>
                  <div class="info-item">
                    <span class="info-label">运输方式：</span>
                    <el-select v-model="orderInfo.transportType" size="small" placeholder="请选择运输方式">
                      <el-option v-for="(item, index) in transportType" :key="index" :label="item" :value="item">
                      </el-option>
                    </el-select>
                  </div>
                </div>
                <div v-else>
                  <div class="info-item">
                    <span class="info-label">结算方式：</span>
                    <span class="info-value">{{ orderInfo.paymentType }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">发票类型：</span>
                    <span class="info-value">{{ orderInfo.invoiceType }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">提货方式：</span>
                    <span class="info-value">{{ orderInfo.deliveryType }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">运输方式：</span>
                    <span class="info-value">{{ orderInfo.transportType }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">发货门店：</span>
                    <span class="info-value">{{ orderInfo.realSellerName }}/{{ orderInfo.realSellerPhone }}</span>
                  </div>
                  <div class="info-item" v-if="orderInfo.splitBy">
                    <span class="info-label">拆单类型：</span>
                    <span class="info-value">{{ orderInfo.splitBy }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">创建人：</span>
                    <span class="info-value">{{ orderInfo.createdByName }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">创建时间：</span>
                    <span class="info-value">{{ orderInfo.createdAt }}</span>
                  </div>
                </div>
              </div>
              <div class="remarks-column">
                <div class="remarks-label">买家备注：</div>
                <el-input
                  type="textarea"
                  class="remarks-input"
                  placeholder="请输入你要备注的内容"
                  v-model="orderInfo.remarks"
                  maxlength="200"
                  :disabled="![11].includes(activeStep)"
                  clearable
                  show-word-limit
                  :autosize="{ minRows: 5, maxRows: 5 }"
                >
                </el-input>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 积分/优惠券抵扣 -->
      <div class="discount-section" v-if="[11].includes(activeStep)">
        <div class="section-card">
          <div class="card-header">
            <div class="card-title">
              <i class="el-icon-discount"></i>
              <span>积分/优惠券抵扣</span>
            </div>
          </div>
          <div class="card-content">
            <!-- 优惠券部分 -->
            <div class="coupon-section">
              <div class="coupon-header">
                <div class="coupon-title">
                  <span>优惠券</span>
                  <el-tooltip placement="top">
                    <div slot="content" class="white-space">{{ selectCouponInfo.description }}</div>
                    <i class="el-icon-info coupon-icon"></i>
                  </el-tooltip>
                  <span class="more-coupon" @click="toMoreCoupon">领取更多优惠券>></span>
                </div>
                <div class="coupon-action">
                  <div v-if="!couponList.length" class="no-coupon">暂无可用优惠券</div>
                  <template v-else>
                    <div class="selected-coupon">
                      <div v-if="selectCouponInfo.id" class="coupon-display">
                        <span class="coupon-tag">{{ selectCouponInfo.content }}</span>
                        <span class="coupon-amount">-¥{{ Number(selectCouponInfo.amount).toFixed(2) }}</span>
                      </div>
                      <div class="toggle-coupon" @click="showCouponList = !showCouponList">
                        <span>{{ showCouponList ? "收起" : "展开" }}</span>
                        <i :class="showCouponList ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <CouponSelect
                v-if="showCouponList && couponList.length"
                :couponList="couponList"
                :couponId="couponId"
                @couponChange="couponChange"
              />
            </div>

            <!-- 积分部分 -->
            <div class="points-section" v-if="pointsData && pointsData.enable">
              <div class="points-header">
                <div class="points-title">
                  <span>积分抵扣</span>
                  <el-tooltip placement="top">
                    <div slot="content" class="white-space">
                      使用积分抵扣，实际可抵扣金额后按提交时剩余可用积分计算。
                    </div>
                    <i class="el-icon-info points-icon"></i>
                  </el-tooltip>
                  <span v-if="!pointsData.isDetail">（{{ pointsData.points }}分）</span>
                </div>
                <div class="points-action">
                  <el-checkbox v-model="usePoints" v-if="pointsData.availablePoints">
                    本次可用{{ pointsData.availablePoints }}积分抵扣{{ pointsData.availableDeductAmount }}元
                  </el-checkbox>
                  <span v-else class="no-points">暂无可用积分</span>
                </div>
              </div>
            </div>

            <!-- 积分赠送 -->
            <div class="points-gift">
              <div class="gift-info">
                <span class="gift-tag">积分</span>
                <span class="gift-text"
                  >赠送 <span class="gift-amount">{{ pointsData.incomePoints }}</span> 积分</span
                >
              </div>
              <div class="gift-note">订单完成后积分 +{{ pointsData.incomePoints }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 价格汇总 -->
      <div class="price-summary">
        <div class="summary-card">
          <div class="summary-content" v-if="[11].includes(activeStep)">
            <div class="summary-item">
              <span class="summary-label">总商品金额</span>
              <span class="summary-value">{{ toFixedFn(totalAmount) }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">积分抵扣</span>
              <span class="summary-value" v-if="usePoints && pointsData.availablePoints">
                -{{ toFixedFn(pointsData.availableDeductAmount) }}
              </span>
              <span class="summary-value" v-else>0</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">优惠劵抵扣</span>
              <span class="summary-value" v-if="selectCouponInfo.id">-{{ toFixedFn(selectCouponInfo.amount) }}</span>
              <span class="summary-value" v-else>0</span>
            </div>
          </div>
          <div class="summary-content" v-else>
            <div class="summary-item">
              <span class="summary-label">商品总价</span>
              <span class="summary-value">{{ toFixedFn2(totalAmount) }}</span>
            </div>
            <div v-if="couponInfo && couponInfo.id" class="summary-item">
              <span class="summary-label">优惠券</span>
              <span class="summary-value">
                <span class="coupon-tag-small">{{ couponInfo.content }}</span>
                <span class="coupon-amount-small">-¥{{ Number(couponInfo.amount).toFixed(2) }}</span>
              </span>
            </div>
            <div v-if="pointsData && pointsData.enable" class="summary-item">
              <span class="summary-label">积分抵扣</span>
              <span class="summary-value" v-if="pointsData.availablePoints">
                本次使用{{ pointsData.availablePoints }}积分抵扣{{ pointsData.availableDeductAmount }}元
              </span>
              <span v-else class="summary-value">暂无可用积分</span>
            </div>
            <div v-if="pointsData && pointsData.enable && pointsData.incomePoints" class="summary-item">
              <span class="summary-label">
                <span class="gift-tag-small">积分</span>
              </span>
              <span class="summary-value">订单完成后积分 +{{ pointsData.incomePoints }}</span>
            </div>
            <template v-if="[9].includes(activeStep)">
              <div class="summary-item">
                <span class="summary-label">实付金额</span>
                <span class="summary-value">{{ toFixedFn(orderInfo.paidAmount) }}</span>
              </div>
              <div class="summary-item total-pay">
                <span class="summary-label">应付金额</span>
                <span class="summary-value total-amount">{{ toFixedFn(orderInfo.payAmount) }}</span>
              </div>
            </template>
            <template v-else-if="![11, 9].includes(activeStep)">
              <div class="summary-item">
                <span class="summary-label">应付金额</span>
                <span class="summary-value">{{ toFixedFn(orderInfo.payAmount) }}</span>
              </div>
              <div class="summary-item total-pay">
                <span class="summary-label">实付金额</span>
                <span class="summary-value total-amount">{{ toFixedFn(orderInfo.paidAmount) }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 页面底部操作区 -->
    <div class="page-footer" id="order-list-page-footer" v-if="[11].includes(activeStep)">
      <div class="footer-content">
        <div class="footer-left">
          <template v-if="!isBindErp && false">
            <div class="erp-bind-tip">
              <span>绑定思锐账ERP号，实现ERP与订货系统订单互联，节省ERP录入采购单时间</span>
              <el-button type="text" size="small" @click="toBindErp">去绑定</el-button>
            </div>
          </template>
        </div>
        <div class="footer-right">
          <div class="order-summary">
            <div class="summary-top">
              <span>共{{ totalCategories }}种，{{ totalQty }}件商品</span>
              <span class="total-price">总价：￥{{ Number(amount.total).toFixed(2) }}</span>
            </div>
            <div class="summary-bottom">
              <span class="original-price-line">原价：{{ toFixedFn(amount.originalPrice) }}</span>
              <span class="discount-info">
                已优惠：{{ Number(amount.deductAmount) > 0 ? toFixedFn(amount.deductAmount) : 0 }}
              </span>
            </div>
          </div>
          <el-button
            v-loading="subLoading"
            type="warning"
            size="small"
            class="submit-order-btn"
            round
            @click="debouncedSubmitOrder"
          >
            提交订单(F2)
          </el-button>
        </div>
      </div>
    </div>
    <div class="page-footer" id="order-list-page-footer" v-else>
      <div class="footer-actions">
        <el-button
          type="primary"
          size="small"
          v-if="[1, 2, 3, 4, 5, 6, 7, 9, 10, 11].includes(activeStep)"
          @click="orderLogFn"
        >
          订单日志(Alt+L)
        </el-button>
        <el-button
          type="primary"
          size="small"
          v-if="[1, 2, 3, 4, 5, 6, 7, 9, 10, 11].includes(activeStep)"
          @click="exportFn"
        >
          导出订单(O/Alt+O)
        </el-button>
        <el-button type="primary" size="small" v-if="[5, 6, 7].includes(activeStep)" @click="deleteFn">
          删除订单(F3)
        </el-button>
        <el-button type="primary" size="small" v-if="[1, 9].includes(activeStep)" @click="cancelFn">
          取消订单(ESC)
        </el-button>
        <el-button type="primary" size="small" v-if="[1, 2, 11].includes(activeStep)" @click="updateFn">
          修改订单(F12)
        </el-button>
        <el-button type="primary" size="small" v-if="[9].includes(activeStep)" @click="toPayFn">
          去付款(F9)
        </el-button>
        <el-button
          type="primary"
          size="small"
          v-if="[9].includes(activeStep) && ['待付款-待卖家审核', '待付款-审核不通过'].includes(activeStatusName)"
          @click="payInfoFn"
        >
          支付信息
        </el-button>
        <el-button type="primary" size="small" v-if="[2, 4].includes(activeStep)" @click="buyAgainFn">
          再次购买(Alt+F)
        </el-button>
        <el-button type="primary" size="small" v-if="[3].includes(activeStep)" @click="logisticInfoFn">
          物流信息(Alt+A)
        </el-button>
        <el-button type="primary" size="small" v-if="[3].includes(activeStep)" @click="confirmReceiptFn">
          确认收货(F10)
        </el-button>
      </div>
    </div>

    <!-- 弹窗组件 -->
    <BindErpBox :visible.sync="bindErpVisible" />
    <OrderLogBox :visible.sync="orderLogVisible" :curRow="orderInfo" />
    <LogisticInfoBox :visible.sync="logisticInfoVisible" :curRow="orderInfo" />
    <ConfirmReceiptBox :visible.sync="confirmReceiptVisible" :curRow="orderInfo" @refresh="refreshFn" />
    <AppointBox :visible.sync="appointVisible" :curRow="orderInfo" />
    <PlaceOrderType ref="placeOrderType" />
    <PayInfoBox :visible.sync="payInfoVisible" :curRow="orderInfo" />
    <GoodsLimitBox ref="goodsLimitBoxRef" />
  </div>
</template>

<script>
import Card from "../components/Card.vue";
import EditAddress from "../components/EditAddress.vue";
import OrderDetailItem from "../components/OrderDetailItem.vue";
import BindErpBox from "./components/BindErpBox.vue";
import OrderLogBox from "../components/OrderLogBox.vue";
import PayInfoBox from "../components/PayInfoBox.vue";
import LogisticInfoBox from "../components/LogisticInfoBox.vue";
import ConfirmReceiptBox from "../components/ConfirmReceiptBox.vue";
import AppointBox from "../components/AppointBox.vue";
import PayCountdown from "../components/PayCountdown.vue";
import Countdown from "../components/Countdown.vue";
import PlaceOrderType from "@/components/PlaceOrderType";
import GoodsLimitBox from "@/components/GoodsLimitBox";
import CouponSelect from "@/components/Coupon/CouponSelect.vue";
// import { observeElementVisibility } from "@/views/newProducts/minin/homepage.js";
import { throttle } from "lodash";
import {
  getBeforeCreateOrderParams,
  createOrder,
  getOrderDetail,
  updateOrders,
  postOrdersExport,
  postOrdersDelete
} from "@/api/order";
import { getPointsDeduct } from "@/api/points";
// import { getCustomerCouponDetail } from "@/api/coupon";

export default {
  name: "orderDetail",
  components: {
    Card,
    EditAddress,
    OrderDetailItem,
    BindErpBox,
    OrderLogBox,
    PayInfoBox,
    LogisticInfoBox,
    ConfirmReceiptBox,
    AppointBox,
    PayCountdown,
    Countdown,
    CouponSelect,
    PlaceOrderType,
    GoodsLimitBox
  },
  data() {
    return {
      // observer: null,
      keyName: "*",
      keyScope: "OrderDetails",
      // 当前状态
      activeStep: 11,
      // 步骤
      stepOptions: [
        { id: 11, name: "提交订单" },
        { id: 1, name: "待确认" },
        { id: 9, name: "待付款" },
        { id: 2, name: "待发货" },
        { id: 3, name: "待收货" },
        // { id: 10, name: "待服务" },
        { id: 4, name: "已完成" },
        { id: 6, name: "卖家取消" },
        { id: 5, name: "买家取消" },
        { id: 7, name: "系统取消" }
      ],
      // 提货方式
      deliveryType: [],
      // 运输方式
      transportType: [],
      // 订单数据
      orderInfo: {},
      addressInfo: {},
      //是否已绑定erp
      isBindErp: false,
      // 弹窗
      bindErpVisible: false,
      orderLogVisible: false,
      logisticInfoVisible: false,
      confirmReceiptVisible: false,
      appointVisible: false,
      // 路由参数
      query: {},
      // 其他页面下订单传入到此页面的数据
      info: {},
      // 商品明细表
      goodsDetailsTable: [],
      // 提交订单前获取的参数
      beforeCreateOrderParams: {},
      // 提交订单前获取的提交参数
      getBeforeCreateOrderParamsData: {},
      // 提交状态
      subLoading: false,
      // 支付信息
      payInfoVisible: false,

      // 优惠券，积分
      couponList: [],
      showCouponList: true,
      couponId: 0,
      couponInfo: {
        id: 0,
        content: "",
        amount: "",
        description: ""
      },

      pointsData: {
        availableDeductAmount: 0,
        availablePoints: 0,
        points: 0,
        incomePoints: 0,
        enable: true,
        isDetail: false
      },
      usePoints: true, // 是否使用积分
      count: 0
    };
  },
  computed: {
    activeTitle() {
      return this.stepOptions.find(item => item.id === this.activeStep).name;
    },
    // 当前状态名称
    activeStatusName() {
      return this.orderInfo.statusName;
    },
    // 总种类数
    totalCategories() {
      if (this.query.info) {
        return this.beforeCreateOrderParams.totalCategories;
      } else {
        return this.goodsDetailsTable.length;
      }
    },
    // 总件数
    totalQty() {
      if (this.query.info) {
        return this.beforeCreateOrderParams.totalQty;
      } else {
        return this.orderInfo.totalQty;
      }
    },
    // 总金额
    totalAmount() {
      let totalAmount = "";
      if (this.query.info) {
        totalAmount = Number(this.beforeCreateOrderParams.totalAmount).toFixed(2);
      } else {
        totalAmount = Number(this.orderInfo.totalAmount).toFixed(2);
      }
      return isNaN(totalAmount) ? "议价" : totalAmount;
    },
    // 选中的优惠券(activeStep:11 待提交)
    selectCouponInfo() {
      return (
        this.couponList.find(item => item.id === this.couponId) || {
          id: 0,
          content: "",
          amount: "",
          description: ""
        }
      );
    },
    // 合计金额(activeStep:11 待提交)
    amount() {
      const data = {
        total: "0", // 合计金额
        originalPrice: "0", // 原价
        deductAmount: this.selectCouponInfo.amount || "0" // 已优惠金额
      };

      const totalFee = this.beforeCreateOrderParams.totalAmount || "0";
      let totalOriginalPrice = 0.0; // 总原价，通过originalPrice计算
      let promotionDeductAmount = 0.0; // 促销优惠

      this.beforeCreateOrderParams.rows?.forEach(cart => {
        totalOriginalPrice += parseFloat(cart.goods.originalPrice) * parseInt(cart.qty);
        promotionDeductAmount +=
          (parseFloat(cart.goods.originalPrice) - parseFloat(cart.goods.price)) * parseInt(cart.qty);
        // if (cart.checkedInstall) {
        //   totalFee += parseFloat(cart.installCost) * parseInt(cart.qty);
        //   totalOriginalPrice += parseFloat(cart.installCost) * parseInt(cart.qty);
        // }
      });

      if (this.selectCouponInfo?.id) {
        data.total = (Number(totalFee) - parseFloat(this.selectCouponInfo?.amount)).toString();
      } else {
        data.total = totalFee + "";
      }

      if (this.usePoints) {
        data.total = (Number(data.total) - this.pointsData.availableDeductAmount).toString();
        data.deductAmount = (Number(data.deductAmount) + this.pointsData.availableDeductAmount).toString();
      }
      if (promotionDeductAmount > 0) {
        data.deductAmount = (Number(data.deductAmount) + Number(promotionDeductAmount)).toFixed(2);
      }
      data.originalPrice = totalOriginalPrice.toFixed(2);
      // console.log("data", data);

      return data;
    },
    // 结算方式
    paymentType() {
      return this.beforeCreateOrderParams.paymentType;
    },
    // 发票类型
    invoiceType() {
      return this.beforeCreateOrderParams.invoiceType;
    }
  },
  watch: {
    couponId: {
      handler() {
        this.refreshPointsData();
      }
    },
    activeStep: {
      handler(val) {
        if (val === 1 && this.count <= 6) {
          setTimeout(() => {
            this.reqLoopGetOrderDetail();
          }, 1000);
        }
      },
      deep: true
    }
  },
  created() {
    this.debouncedSubmitOrder = throttle(this.submitOrder, 1000, {
      leading: true, // 开始时立即执行
      trailing: false // 结束时不再执行
    });
    this.query = this.$route.query;
    this.setInfo();
    if (this.$route.query.id) {
      this.getOrderDetailFn();
    }
  },
  mounted() {
    setTimeout(() => {
      this.$hotkeys(this.keyName, this.keyScope, this.handleKeyCode);
      this.$hotkeys.setScope(this.keyScope);
      // const pageContent = document.getElementById("pageContentId");
      // if (pageContent) {
      //   pageContent.scrollTo({
      //     top: 0,
      //     behavior: "smooth"
      //   });
      // }
    }, 500);
    // const element = document.getElementById("order-list-page-footer");
    // this.observer = observeElementVisibility(element, (isVisible, visibilityRatio) => {
    //   if (isVisible) {
    //     console.log(`页脚进入视口，可见度: ${Math.round(visibilityRatio * 100)}%`);
    //     // 在这里处理页脚出现时的逻辑
    //   } else {
    //     console.log("页脚离开视口");
    //     // 在这里处理页脚消失时的逻辑
    //   }
    // });
  },
  beforeDestroy() {
    this.debouncedSubmitOrder.cancel();
    this.$hotkeys.unbind(this.keyName, this.keyScope);
    // if (this.observer) {
    //   this.observer.disconnect();
    // }
  },
  methods: {
    toMoreCoupon() {
      this.$router.push("/new-products/coupon-center");
    },
    finalAmountFn() {
      return Number(this.orderInfo.finalOriginalPrice) > 0
        ? (Number(this.orderInfo.finalOriginalPrice) - Number(this.orderInfo.concessionsAmount)).toFixed(2)
        : 0;
    },
    // 重新获取积分数据
    refreshPointsData() {
      const amount1 = this.usePoints
        ? Number(this.amount.total || 0) + Number(this.pointsData.availableDeductAmount || 0)
        : this.amount.total;
      getPointsDeduct({ amount: amount1 })
        .then(res => {
          this.pointsData = {
            availableDeductAmount: Number(res.availableDeductAmount || 0),
            availablePoints: Number(res.availablePoints || 0),
            points: Number(res.points || 0),
            incomePoints: Number(res.incomePoints || 0),
            enable: res.enable,
            isDetail: false
          };
        })
        .catch(err => {
          this.$message.error(err?.message || "加载失败");
        });
    },
    // 选择优惠券
    couponChange(id) {
      this.couponId = id;
    },
    // 是否使用积分
    usePointsFlagChangeFn(isUsed) {
      this.usePoints = isUsed;
    },
    // 待确认时，每隔1秒发起请求 6次 过后停止请求
    reqLoopGetOrderDetail() {
      this.getOrderDetailFn();
      this.count++;
      if (this.activeStep == 1 && this.count < 6) {
        setTimeout(() => {
          this.reqLoopGetOrderDetail();
        }, 1000);
      }
    },
    handleKeyCode(e) {
      switch (e.code) {
        // 提交订单
        case "F2":
          e.preventDefault();
          e.stopPropagation();
          if ([11].includes(this.activeStep) && !this.subLoading) {
            this.debouncedSubmitOrder();
          }
          break;
        // 删除订单
        case "F3":
          e.preventDefault();
          e.stopPropagation();
          if ([5, 6, 7].includes(this.activeStep)) {
            this.deleteFn();
          }
          break;
        // 去支付
        case "F9":
          e.preventDefault();
          e.stopPropagation();
          if ([9].includes(this.activeStep) && this.orderInfo.toCancelSecond) {
            this.toPayFn();
          }
          break;
        // 确认收货
        case "F10":
          e.preventDefault();
          e.stopPropagation();
          if ([3].includes(this.activeStep)) {
            this.confirmReceiptFn();
          }
          break;
        // 修改订单
        case "F12":
          e.preventDefault();
          e.stopPropagation();
          if ([1, 2, 11].includes(this.activeStep)) {
            this.updateFn();
          }
          break;
        // 订单日志
        case "KeyL":
          e.preventDefault();
          e.stopPropagation();
          if (e.altKey && [1, 2, 3, 4, 5, 6, 7, 9, 10, 11].includes(this.activeStep)) {
            this.orderLogFn();
          }
          break;
        // 导出订单
        case "KeyO":
          e.preventDefault();
          e.stopPropagation();
          if ([1, 2, 3, 4, 5, 6, 7, 9, 10, 11].includes(this.activeStep)) {
            this.exportFn();
          }
          break;
        // 取消订单
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          if ([1, 9].includes(this.activeStep)) {
            this.cancelFn();
          }
          break;
        // 再次购买
        case "KeyF":
          e.preventDefault();
          e.stopPropagation();
          if (e.altKey && [2, 4].includes(this.activeStep)) {
            this.buyAgainFn();
          }
          break;
        // 物流信息
        case "KeyA":
          e.preventDefault();
          e.stopPropagation();
          if (e.altKey && [3].includes(this.activeStep)) {
            this.logisticInfoFn();
          }
          break;
        // 去预约
        case "KeyB":
          e.preventDefault();
          e.stopPropagation();
          if (e.altKey && [10].includes(this.activeStep)) {
            this.toAppointFn();
          }
          break;
        // 申请售后
        case "KeyC":
          e.preventDefault();
          e.stopPropagation();
          if (e.altKey && [4, 10].includes(this.activeStep)) {
            this.applyServiceFn();
          }
          break;
      }
    },
    renderActiveStep(item) {
      if (this.activeStep === item.id) {
        return "font-size-24 font-warning font-weight-bold";
      } else if ([4, 5, 6, 7].includes(this.activeStep) && item.id === 4) {
        return "font-size-24 font-warning font-weight-bold";
      }
    },
    //金额保留两位小数
    toFixedFn(amount) {
      if (amount === "0" || Number(amount) === 0) {
        return "- -";
      }
      return isNaN(Number(amount)) ? "议价" : `￥${Number(amount).toFixed(2)}`;
    },
    toFixedFn2(amount) {
      if (amount === "0" || Number(amount) === 0) {
        return "￥0";
      }
      return isNaN(Number(amount)) ? "议价" : `￥${Number(amount).toFixed(2)}`;
    },
    // 设置从下单页面进入此页面传入的参数
    setInfo() {
      if (this.query && this.query.info) {
        this.info = JSON.parse(decodeURIComponent(this.query.info));
        this.getBeforeCreateOrderParams();
      }
    },
    // 获取创建订单前的参数
    getBeforeCreateOrderParams() {
      let type = 0;
      let carts = [];
      let skus = [];
      let allCarts = false;
      // 商品详情页下单
      if (this.info.type === "productsDetails") {
        type = 3;
        skus = [this.info.data];
      }
      // 购物车页下单
      if (this.info.type === "shoppingCart") {
        type = 1;
        carts = this.info.data;
        console.log("info", this.info);
        allCarts = this.info.allCarts;
      }
      // 订单再次购买
      if (this.info.type === "orderList") {
        type = 3;
        skus = this.info.data;
      }
      const data = {
        type: type, // 获取方式 1.购物车自己采购下单 2.购物车代他人开单 3.查询页直接下单 4.代理客户获取确认订单参数 5.待卖家审核状态下，买家修改订单明细中的订货数量，获取修改参数
        carts: carts, // 购物车信息
        skus: skus, // 查询商品信息
        dict: true, // 是否需要字典信息
        allCarts: allCarts
      };
      this.getBeforeCreateOrderParamsData = data;
      getBeforeCreateOrderParams(data)
        .then(res => {
          this.beforeCreateOrderParams = res || {};
          this.deliveryType = res.deliveryType || [];
          this.transportType = res.transportType || [];
          this.addressInfo = {
            type: "orderDetail"
          };

          this.setGoodsDetailsTable();

          this.pointsData = {
            availableDeductAmount: Number(res.availableDeductAmount || 0),
            availablePoints: Number(res.availablePoints || 0),
            points: Number(res.points || 0),
            incomePoints: Number(res.incomePoints || 0),
            enable: res.enable,
            isDetail: false
          };

          this.couponList = res.coupons || [];
          if (res.coupons?.length) {
            this.couponId = res.coupons[0].id;
          }
        })
        .catch(err => {
          this.$message.error(err.message || "参数获取失败");
        });
    },
    setGoodsDetailsTable() {
      const list = [];
      this.beforeCreateOrderParams.rows.map(item => {
        list.push({
          id: item.goods.id,
          type: item.goods.type,
          code: item.goods.code,
          name: item.goods.name,
          subject: item.goods.subject,
          brand: item.goods.brand,
          productionPlace: item.goods.productionPlace,
          originalPrice: item.goods.originalPrice,
          price: item.goods.price,
          qty: item.qty,
          amount: item.amount,
          warehouse: item.warehouse,
          productBatch: item.productBatch,
          category: item.goods.category,
          vehModel: item.goods.vehModel,
          property: item.goods.property,
          unitName: item.goods.unit, //单位
          insurCertType: item.goods.insurCertType, //品质
          model: item.goods.model,
          productionCode: item.goods.productionCode,
          goodsSpecification: item.goodsSpecification, //购买规格参数
          images: item.goods.images, //图片
          extaCode: item.goods.extaCode, //互换件
          promotion: item.goods.promotion, //促销
          coupons: item.goods.coupons //优惠券
        });
      });
      this.goodsDetailsTable = list;
    },
    getOrderDetailFn() {
      getOrderDetail({ id: this.query.id })
        .then(res => {
          this.orderInfo = res || {};
          this.activeStep = res.status;
          this.goodsDetailsTable =
            res.details.map(item => ({
              ...item,
              amount: (Number(item.qty) * Number(item.price)).toFixed(2)
            })) || [];
          if (res.concessions && res.concessions.coupon) {
            this.couponInfo = res.concessions.coupon;
          }
          this.pointsData = {
            availableDeductAmount: Number(res.concessions?.point?.amount || 0),
            availablePoints: Number(res.concessions?.point?.points || 0),
            points: Number(res.points || 0),
            incomePoints: Number(res.incomePoints || 0),
            enable: res.enable,
            isDetail: true
          };
        })
        .catch(err => {
          this.$message.error(err?.message);
        });
    },
    //刷新列表
    refreshFn() {
      this.getOrderDetailFn();
    },

    //商品明细
    tableDataChange(rows) {
      this.orderInfo.details = JSON.parse(JSON.stringify(rows));
    },
    //返回
    toBack() {
      this.$router.go(-1);
    },
    //选择地址
    selectAddress(data) {
      this.orderInfo.addressId = data.id;
    },
    //去绑定erp
    toBindErp() {
      this.bindErpVisible = true;
    },
    //返回订货列表并刷新
    replaceOrderList() {
      this.$router.replace({
        path: "/new-products/system-center/order-list",
        query: {
          type: "refresh"
        }
      });
    },
    //提交订单
    submitOrder: function() {
      if (!this.orderInfo.addressId) {
        this.$message.warning("请选择收货地址或者创建！");
        return;
      }
      if (this.subLoading) {
        return;
      }
      const info = {
        ...this.info,
        isSubmitOrder: true
      };
      // 商品限购
      this.$refs.goodsLimitBoxRef.show(info, data => {
        let orderInfo = {};
        if (data.type === "ongoing") {
          orderInfo = {
            carts: this.getBeforeCreateOrderParamsData.carts, // 购物车
            skus: this.getBeforeCreateOrderParamsData.skus // 查询商品
          };
          this.createOrderFn(orderInfo);
        } else if (data.type === "submitOrder") {
          orderInfo = data.orderInfo;
          this.createOrderFn(orderInfo);
        }
      });
    },
    createOrderFn(orderInfo) {
      // const prices = this.goodsDetailsTable.map(item => item.price);
      const data = {
        ...orderInfo,
        addressId: this.orderInfo.addressId, // 地址ID
        // carts: this.getBeforeCreateOrderParamsData.carts, // 购物车
        // skus: this.getBeforeCreateOrderParamsData.skus, // 查询商品
        type: 0, // 订单类型 0: 普通订单 1: 急件订单 2: 补货订单 3: 批量订单 4: 现货订单 5: 期货订单
        buyerConfirm: false, // 是否需要买家确认或付款 true/false
        paymentType: this.beforeCreateOrderParams.paymentType, // 结算方式
        invoiceType: this.beforeCreateOrderParams.invoiceType, // 发票类型
        deliveryType: this.orderInfo.deliveryType, // 提货方式
        transportType: this.orderInfo.transportType, // 运输方式
        packType: "", // 包装方式
        remarks: this.orderInfo.remarks, // 备注
        priceLevels: [],
        // prices: prices, // 商品当前价格，用于下单时检查价格是否变动
        couponId: this.couponId, // 使用的已领优惠券ID
        consumePoints: this.usePoints ? this.pointsData.availablePoints : 0, // 消耗积分
        toStoreService: false, // 是否到店服务
        CustomerOrderCode: "",
        stockoutOperation: 0, // 缺货处理，orderType=5（缺货商品）时，该参数有效。1:缺货配件取消；2:缺货配件转期货订单；3:缺货配件转预约
        allCarts: this.info.allCarts
      };
      this.subLoading = true;
      createOrder(data)
        .then(res => {
          this.subLoading = false;
          // 跳转待确认
          if (res && res.length) {
            this.$message.success("提交成功");
            this.$bus.$emit("shoppingCartRefresh");
            if (res.length > 1) {
              const message = `<div class="text-center">由于库存或其他原因<br/>您的订单已拆分为多张订单您将收到不同的发货通知。</div>`;
              const option = { dangerouslyUseHTMLString: true };
              this.$alert(message, "提示", option)
                .then(() => {
                  this.replaceOrderList();
                })
                .catch(() => {
                  this.replaceOrderList();
                });
            } else {
              this.$router.replace({
                path: "/order/public",
                query: {
                  id: res[0].id,
                  url: "/new-products/system-center/order-list/order-details"
                }
              });
            }
          } else {
            this.$message.error("提交订单失败！");
          }
        })
        .catch(err => {
          this.subLoading = false;
          this.$message.error(err.message || "提交订单失败！");
        });
    },
    //订单日志
    orderLogFn() {
      this.orderLogVisible = true;
    },
    // 支付信息
    payInfoFn() {
      this.payInfoVisible = true;
    },
    //取消订单
    cancelFn() {
      this.$confirm(`是否确认取消此订单？`, "提示", { type: "warning" })
        .then(() => {
          const params = {
            id: this.orderInfo.id,
            status: 5 //5 : 买家取消
          };
          updateOrders(params)
            .then(() => {
              this.$message.success("取消成功");
              this.replaceOrderList();
            })
            .catch(() => {
              this.$message.error("取消失败");
            });
        })
        .catch(() => {});
    },
    //删除订单
    deleteFn() {
      this.$confirm(`是否确认删除此订单？`, "提示", { type: "warning" })
        .then(() => {
          const params = {
            ids: [this.orderInfo.id]
          };
          postOrdersDelete(params)
            .then(() => {
              this.$message.success("删除成功");
              this.replaceOrderList();
            })
            .catch(err => {
              this.$message.error(err?.message || "删除失败");
            });
        })
        .catch(() => {});
    },
    //导出订单
    exportFn() {
      this.$confirm(`是否确认导出此订单？`, "提示", { type: "warning" })
        .then(() => {
          const params = {
            ids: this.orderInfo.id + "",
            exportType: "orderDetail" // 导出类型 order: 订单 orderDetail: 订单明细
          };
          this.reqOrdersExport(params);
        })
        .catch(() => {});
    },
    reqOrdersExport(params) {
      postOrdersExport(params)
        .then(res => {
          const url = window.URL.createObjectURL(new Blob([res]));
          const link = document.createElement("a");
          link.href = url;
          const name = `订单列表_${this.$dayjs().format("YYYY-MM-DD")}.xls`;
          link.setAttribute("download", name);
          link.click();
          this.$message.success("导出成功！");
        })
        .catch(err => {
          this.$message.error(err?.message || "导出失败");
        });
    },
    //修改订单
    updateFn() {
      let param = {};
      if ([1, 2].includes(this.activeStep)) {
        //待确认、待发货
        param = {
          path: "/new-products/system-center/order-list/modify-order-detail1",
          query: {
            id: this.orderInfo.id
          }
        };
      }
      this.$router.push(param);
    },
    //去付款
    toPayFn() {
      const obj = {
        ids: this.orderInfo.id + ""
      };
      const param = {
        path: "/new-products/system-center/order-list/order-pay",
        query: {
          data: encodeURIComponent(JSON.stringify(obj))
        }
      };
      this.$router.push(param);
    },
    //再次购买
    buyAgainFn() {
      const data = window.tool.deepClone(this.orderInfo);
      data.details = data.details.filter(item => item.originalDetailId == 0);
      this.$refs.placeOrderType.show({
        type: "orderList",
        data: data
      });
    },
    //物流信息
    logisticInfoFn() {
      this.logisticInfoVisible = true;
    },
    //确认收货
    confirmReceiptFn() {
      this.confirmReceiptVisible = true;
    },
    //收货确认
    // sureReceiptFn(data) {
    //   console.log("收货确认", data);
    // },
    //去预约
    toAppointFn() {
      this.appointVisible = true;
    },
    //申请售后
    applyServiceFn() {
      const param = {
        path: "/order/after-sale",
        query: {
          id: this.orderInfo.id
        }
      };
      this.$router.push(param);
    }
  }
};
</script>

<style lang="less" scoped>
.order-detail-page {
  background-color: #f5f7fa;
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  .page-header {
    background: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border-bottom: 1px solid #e6e8eb;

    .header-content {
      display: flex;
      align-items: center;
      padding: 12px 20px;

      .back-btn {
        display: flex;
        align-items: center;
        color: #606266;
        cursor: pointer;
        transition: color 0.3s;

        &:hover {
          color: var(--color-primary);
        }

        i {
          margin-right: 4px;
        }
      }

      .page-title {
        flex: 1;
        text-align: center;
        font-size: 18px;
        font-weight: 600;
        color: #303133;
      }
    }
  }

  .page-content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
  }

  .step-indicator {
    margin-bottom: 20px;

    .step-container {
      display: flex;
      justify-content: center;
      align-items: center;
      background: #fff;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

      .step-item {
        display: flex;
        align-items: center;
        position: relative;

        .step-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #dcdfe6;
          transition: all 0.3s;

          &.active {
            background: var(--color-primary);
            transform: scale(1.2);
          }
        }

        .step-text {
          margin: 0 12px;
          color: #909399;
          font-size: 14px;
          transition: color 0.3s;

          &.active {
            color: var(--color-primary);
            font-weight: 500;
          }
        }

        .step-connector {
          width: 60px;
          height: 2px;
          background: #dcdfe6;
        }
      }
    }
  }

  .countdown-section {
    margin-bottom: 20px;

    .countdown-card {
      background: linear-gradient(135deg, #ff6b6b, #ff8e53);
      border-radius: 8px;
      padding: 16px 20px;
      color: #fff;
      box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);

      .countdown-content {
        display: flex;
        align-items: center;

        .countdown-icon {
          font-size: 24px;
          margin-right: 12px;
        }

        .countdown-info {
          flex: 1;

          .countdown-text {
            font-size: 16px;
            font-weight: 500;
            margin-bottom: 4px;
          }

          .countdown-timer {
            font-size: 14px;
            opacity: 0.9;
          }
        }

        .pay-btn {
          background: #fff;
          color: #ff6b6b;
          font-weight: 500;
          border: none;

          &:hover {
            background: rgba(255, 255, 255, 0.9);
          }
        }
      }
    }
  }

  .order-basic-info {
    margin-bottom: 20px;

    .info-card {
      background: #fff;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

      .info-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;

        .order-code {
          font-size: 16px;
          font-weight: 600;
          color: #303133;
        }

        .submit-info {
          color: #606266;
          font-size: 14px;

          .submit-by {
            margin-right: 20px;
          }
        }
      }

      .info-footer {
        .buyer-name {
          color: #606266;
          font-size: 14px;
        }
      }
    }
  }

  .address-section,
  .goods-section,
  .order-info-section,
  .discount-section {
    margin-bottom: 20px;
  }

  .section-card {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .card-header {
      padding: 16px 20px;
      border-bottom: 1px solid #f0f2f5;

      .card-title {
        display: flex;
        align-items: center;
        font-size: 16px;
        font-weight: 600;
        color: #303133;

        i {
          margin-right: 8px;
          color: var(--color-primary);
        }
      }
    }

    .card-content {
      padding: 20px;
    }
  }

  .address-display {
    .address-main {
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      .receiver {
        font-weight: 500;
        margin-right: 12px;
      }
    }

    .address-detail {
      color: #606266;
      line-height: 1.5;
    }
  }

  .goods-summary {
    padding-top: 16px;
    border-top: 1px solid #f0f2f5;

    .summary-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;

      .total-amount {
        font-weight: 600;
        color: #ff6b6b;
      }

      &.discount-info {
        font-size: 14px;

        .original-price {
          color: #909399;
          text-decoration: line-through;
        }

        .discount-amount {
          color: #67c23a;
        }
      }
    }
  }

  .order-info-content {
    display: flex;

    .info-column {
      flex: 1;
      padding-right: 20px;

      .info-item {
        display: flex;
        align-items: center;
        margin-bottom: 16px;

        .info-label {
          width: 80px;
          font-weight: 500;
          color: #606266;
        }

        .info-value {
          color: #303133;
        }
      }
    }

    .remarks-column {
      flex: 1;

      .remarks-label {
        font-weight: 500;
        color: #606266;
        margin-bottom: 8px;
      }

      .remarks-input {
        ::v-deep .el-textarea__inner {
          border-radius: 4px;
          resize: none;
        }
      }
    }
  }

  .coupon-section {
    .coupon-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .coupon-title {
        display: flex;
        align-items: center;

        .coupon-icon {
          margin: 0 8px;
          color: #909399;
          cursor: pointer;
        }

        .more-coupon {
          color: #ff6b6b;
          cursor: pointer;
          font-size: 14px;

          &:hover {
            text-decoration: underline;
          }
        }
      }

      .coupon-action {
        .no-coupon {
          color: #909399;
        }

        .selected-coupon {
          display: flex;
          align-items: center;

          .coupon-display {
            display: flex;
            align-items: center;

            .coupon-tag {
              background: rgba(255, 107, 107, 0.1);
              color: #ff6b6b;
              border: 1px solid rgba(255, 107, 107, 0.3);
              border-radius: 4px;
              padding: 2px 6px;
              font-size: 12px;
              margin-right: 8px;
            }

            .coupon-amount {
              color: #ff6b6b;
              font-weight: 500;
            }
          }

          .toggle-coupon {
            display: flex;
            align-items: center;
            color: var(--color-primary);
            cursor: pointer;
            margin-left: 12px;

            &:hover {
              color: #66b1ff;
            }
          }
        }
      }
    }
  }

  .points-section {
    padding: 16px 0;
    border-top: 1px solid #f0f2f5;

    .points-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .points-title {
        display: flex;
        align-items: center;

        .points-icon {
          margin: 0 8px;
          color: #909399;
          cursor: pointer;
        }
      }

      .points-action {
        .no-points {
          color: #909399;
        }

        ::v-deep .el-checkbox {
          .el-checkbox__input.is-checked + .el-checkbox__label {
            color: #ff6b6b;
          }

          .el-checkbox__input.is-checked .el-checkbox__inner {
            background-color: #ff6b6b;
            border-color: #ff6b6b;
          }
        }
      }
    }
  }

  .points-gift {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
    border-top: 1px solid #f0f2f5;

    .gift-info {
      display: flex;
      align-items: center;

      .gift-tag {
        background: rgba(64, 158, 255, 0.1);
        color: var(--color-primary);
        border: 1px solid rgba(64, 158, 255, 0.3);
        border-radius: 4px;
        padding: 2px 6px;
        font-size: 12px;
        margin-right: 8px;
      }

      .gift-text {
        color: #606266;

        .gift-amount {
          color: #ff6b6b;
          font-weight: 500;
        }
      }
    }

    .gift-note {
      color: #67c23a;
      font-weight: 500;
    }
  }

  .price-summary {
    margin-bottom: 20px;

    .summary-card {
      background: #fff;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

      .summary-content {
        width: 300px;
        margin-left: auto;

        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;

          .summary-label {
            color: #606266;
          }

          .summary-value {
            color: #303133;

            .coupon-tag-small {
              background: rgba(255, 107, 107, 0.1);
              color: #ff6b6b;
              border: 1px solid rgba(255, 107, 107, 0.3);
              border-radius: 2px;
              padding: 1px 4px;
              font-size: 11px;
              margin-right: 6px;
            }

            .coupon-amount-small {
              color: #ff6b6b;
              font-weight: 500;
            }

            .gift-tag-small {
              background: rgba(64, 158, 255, 0.1);
              color: var(--color-primary);
              border: 1px solid rgba(64, 158, 255, 0.3);
              border-radius: 2px;
              padding: 1px 4px;
              font-size: 11px;
            }
          }

          &.total-pay {
            padding-top: 12px;
            border-top: 1px solid #f0f2f5;

            .summary-label {
              font-size: 16px;
              font-weight: 500;
            }

            .total-amount {
              font-size: 20px;
              font-weight: 600;
              color: #ff6b6b;
            }
          }
        }
      }
    }
  }

  .page-footer {
    background: #fff;
    border-top: 1px solid #e6e8eb;
    padding: 16px 20px;
    position: fixed;
    z-index: 999;
    bottom: 0;
    width: 100%;
    left: 0;
    .footer-content {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .footer-left {
        .erp-bind-tip {
          display: flex;
          align-items: center;
          color: #606266;

          .el-button {
            margin-left: 8px;
          }
        }
      }

      .footer-right {
        display: flex;
        align-items: center;

        .order-summary {
          margin-right: 20px;
          text-align: right;

          .summary-top {
            margin-bottom: 4px;

            .total-price {
              font-weight: 600;
              color: #ff6b6b;
              margin-left: 12px;
            }
          }

          .summary-bottom {
            font-size: 14px;

            .original-price-line {
              color: #909399;
              text-decoration: line-through;
              margin-right: 12px;
            }

            .discount-info {
              color: #67c23a;
            }
          }
        }

        .submit-order-btn {
          padding: 10px 24px;
          font-weight: 500;
        }
      }
    }

    .footer-actions {
      display: flex;
      justify-content: flex-end;
      flex-wrap: wrap;
      gap: 10px;
    }
  }
}

// 响应式适配
@media (max-width: 768px) {
  .order-detail-page {
    .page-content {
      padding: 12px;
    }

    .step-indicator .step-container {
      padding: 16px 12px;

      .step-item .step-connector {
        width: 30px;
      }
    }

    .order-basic-info .info-card .info-header {
      flex-direction: column;
      align-items: flex-start;

      .submit-info {
        margin-top: 8px;
      }
    }

    .order-info-content {
      flex-direction: column;

      .info-column {
        padding-right: 0;
        margin-bottom: 20px;
      }
    }

    .price-summary .summary-card .summary-content {
      width: 100%;
    }

    .page-footer .footer-content {
      flex-direction: column;
      align-items: flex-start;

      .footer-right {
        width: 100%;
        justify-content: space-between;
        margin-top: 12px;
      }
    }

    .page-footer .footer-actions {
      justify-content: flex-start;
    }
  }
}
</style>
